import { useTranslation } from "react-i18next";
import React, { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { apollo } from "../../../../utils/apollo";
import { IMPORT_POLICY_FILE } from "../../../../utils/graphql";
import { ArrowDownTrayIcon } from "@heroicons/react/24/solid";
import ButtonLink from "../../../../components/ButtonLink";
import Loading from "../../../../components/Loading";
import { cn } from "../../../../utils/style";
import { toast } from "react-toastify";
import { http } from "../../../../utils/http";
import { GAID_API_URL, GAID_API_BASE_URL } from "../../../../utils/env";
import Button from "../../../../components/Button";
import FileUpload from "../../../../components/FileUpload";
function SlashdataPolicyImportForm({ integration, onSuccess }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [uploading, setUploading] = useState(false);
  const [errorLines, setErrorLines] = useState([]);
  const [file, setFile] = useState({
    realFileName: null,
    fileName: null,
    fileUrl: null,
    mimeType: null,
  });
  const importPolicyFileMutation = useMutation({
    mutationFn: async () => {
      const { data } = await apollo.mutate({
        mutation: IMPORT_POLICY_FILE,
        variables: {
          input: {
            fileName: file?.fileName,
            integrationUuid: integration?.integrationUuid || null,
            fileUrl: file?.fileUrl || null,
            mimeType: file?.mimeType || null,
          },
        },
      });
      return data?.importPolicyFile;
    },
    onSuccess: (data) => {
      if (data.success && !data?.errorLines?.length) {
        toast.success("File is being processed...");
        queryClient.invalidateQueries(["policy_list"]);
        if (onSuccess) onSuccess(data.fileImportUuid);
      }
      if (data.success && !!data?.errorLines?.length) {
        toast.error("Some policies could not be processed successfully.");
        setErrorLines(data?.errorLines);
      }
    },
    onError: (error) => {
      console.error(error);
      toast.error("An error occurred while initiating the import.");
    },
  });
  const resetFileField = () => {
    setFile({
      realFileName: null,
      fileName: null,
      fileUrl: null,
      mimeType: null,
    });
    setErrorLines([]);
  };
  const handleFileUpload = async (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      const response = await http.post("/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      const { fileName, fileUrl, mimeType } = response?.data;
      if (fileName) {
        setFile({
          fileName,
          fileUrl,
          mimeType,
        });
      }
    } catch (error) {
      console.error(error);
      toast.error("File upload failed");
    }
    setUploading(false);
  };
  return (
    <div>
      <div className="flex flex-col md:flex-row gap-4 my-4 border border-sky-200 bg-sky-50 p-4 rounded-2xl">
        <div className="text-sm text-sky-800">
          {t(
            "Download the member data template, fill the details and re-upload it. Please do not change the name and\n                format of the downloaded file while uploading it. Please use latest XLSX template while uploading the\n                data",
          )}
        </div>
        <div className="shrink-0">
          <ButtonLink
            href={`${GAID_API_BASE_URL}/slashdata_template.xlsx`}
            target="_blank"
            secondary={true}
          >
            {t("Download slashdata.xlsx")}
          </ButtonLink>
        </div>
      </div>

      <div
        className={cn("sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-8 sm:grid")}
      >
        <label className="block text-sm font-black uppercase tracking-widest text-slate-500 sm:pt-1.5">
          {t("CSV/XLSX File")}
          <span className="text-red-500">*</span>
        </label>
        <div className="relative mt-2 sm:col-span-2 sm:mt-0">
          <FileUpload
            id="policy-bulk-upload-file"
            name="policy-bulk-upload-file"
            onChange={handleFileUpload}
            fileName={file?.fileName}
            onClear={resetFileField}
            disabled={uploading}
          />

          {uploading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[2px] rounded-2xl">
              <Loading message="Uploading..." />
            </div>
          )}

          {file?.fileUrl && (
            <div className="mt-4 flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
              <ButtonLink
                href={`${GAID_API_URL}${file?.fileUrl}`}
                target="_blank"
                ghost
                className="flex items-center gap-2 !text-xs !py-2"
              >
                <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                <span>{t("Preview File")}</span>
              </ButtonLink>
            </div>
          )}
        </div>
      </div>

      {!!errorLines?.length && (
        <div className="mt-8 space-y-3">
          <label
            htmlFor="policy-bulk-errors"
            className="block text-sm font-black uppercase tracking-widest text-red-500"
          >
            {t("Error Lines Detected")}
          </label>
          <textarea
            name="policy-bulk-errors"
            id="policy-bulk-errors"
            rows="6"
            className="block w-full rounded-2xl border-slate-200 bg-slate-50 font-mono text-xs text-red-600 shadow-sm focus:border-red-500 focus:ring-red-500 transition-all"
            value={errorLines.join("\n")}
            readOnly={true}
          />
        </div>
      )}

      <div className="mt-10 flex justify-end">
        <Button
          disabled={importPolicyFileMutation.isLoading || !file?.fileName}
          type="button"
          className="w-full sm:w-auto min-w-[200px]"
          onClick={() => {
            setErrorLines([]);
            importPolicyFileMutation.mutate();
          }}
        >
          <span className="flex items-center justify-center gap-2">
            {importPolicyFileMutation.isLoading
              ? "Processing..."
              : "Process File"}
          </span>
        </Button>
      </div>
    </div>
  );
}
export default SlashdataPolicyImportForm;
